import React, { useState, useRef, useEffect } from 'react';
import { ShoppingBag, User, Heart, Search, Menu, X, ArrowRight, ChevronRight, Plus, Play, Star, Zap, Shield, Truck, Instagram, Flame, Skull } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template19 = () => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    // --- ORIGINAL data (desktop) ---
    const desktopCollections = [
        { name: 'Activewear', tagline: 'Move with Confidence', image: 'https://images.unsplash.com/photo-1518310383802-640c2de311b2?w=800&h=1000&fit=crop', count: '86' },
        { name: 'Athleisure', tagline: 'Gym to Street', image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&h=1000&fit=crop', count: '124' },
        { name: 'Loungewear', tagline: 'Comfort First', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=800&h=1000&fit=crop', count: '68' },
    ];

    const desktopProducts = [
        { name: 'High-Waist Leggings', price: 2499, color: 'Black', image: 'https://images.unsplash.com/photo-1518310383802-640c2de311b2?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=500&h=650&fit=crop', tag: 'Bestseller', colors: ['#000', '#3B82F6', '#10B981'] },
        { name: 'Sports Bra - Mesh', price: 1899, color: 'Navy', image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1518310383802-640c2de311b2?w=500&h=650&fit=crop', tag: 'New', colors: ['#1E40AF', '#000', '#EC4899'] },
        { name: 'Performance Tank', price: 1599, color: 'White', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=500&h=650&fit=crop', tag: null, colors: ['#FFF', '#000', '#6B7280'] },
        { name: 'Training Shorts', price: 1799, color: 'Grey', image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=500&h=650&fit=crop', tag: null, colors: ['#6B7280', '#000', '#10B981'] },
        { name: 'Yoga Pants', price: 2299, color: 'Olive', image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=500&h=650&fit=crop', tag: 'Limited', colors: ['#4D7C0F', '#000', '#7C3AED'] },
        { name: 'Track Jacket', price: 3499, color: 'Black', image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=650&fit=crop', tag: 'New', colors: ['#000', '#DC2626', '#3B82F6'] },
        { name: 'Cropped Hoodie', price: 2799, color: 'Rose', image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=500&h=650&fit=crop', tag: null, colors: ['#FB7185', '#000', '#6B7280'] },
        { name: 'Seamless Set', price: 3999, color: 'Sage', image: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=650&fit=crop', tag: 'Set', colors: ['#84CC16', '#000', '#F97316'] },
    ];

    const desktopFeatures = [
        { icon: Zap, title: 'Quick Dry', desc: '4-way stretch fabric' },
        { icon: Shield, title: 'Squat Proof', desc: 'Full coverage guaranteed' },
        { icon: Truck, title: 'Free Shipping', desc: 'On orders over ₹1,499' },
    ];

    const desktopReviews = [
        { text: 'Best leggings I have ever owned. The fit is perfect and they do not roll down during workouts!', name: 'Priya M.', rating: 5 },
        { text: 'Finally found activewear that is both stylish and functional. The quality is amazing.', name: 'Sneha K.', rating: 5 },
        { text: 'Love the seamless collection. So comfortable for yoga and everyday wear.', name: 'Anita R.', rating: 5 },
    ];

    // --- CURRENT data (mobile) ---
    const mobileCategories = [
        { name: 'Leather & Studs', image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=600&h=800&fit=crop', tagline: 'REBEL CORE' },
        { name: 'Distressed Denim', image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=600&h=800&fit=crop', tagline: 'RAW EDGE' },
        { name: 'Graphic Tees', image: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?w=600&h=800&fit=crop', tagline: 'LOUD & PROUD' },
        { name: 'Dark Streetwear', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=600&h=800&fit=crop', tagline: 'AFTER DARK' },
    ];

    const mobileProducts = [
        { name: 'Spiked Leather Jacket', price: 8999, image: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=500&h=650&fit=crop', tag: 'SOLD FAST', color: 'Jet Black' },
        { name: 'Ripped Chain Jeans', price: 4500, image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=500&h=650&fit=crop', tag: null, color: 'Washed Black' },
        { name: 'Skull Print Oversized Tee', price: 2200, image: 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?w=500&h=650&fit=crop', tag: 'DROP', color: 'Blood Red' },
        { name: 'Combat Cargo Pants', price: 5200, image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=500&h=650&fit=crop', tag: null, color: 'Military' },
        { name: 'Mesh Panel Hoodie', price: 3800, image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=650&fit=crop', tag: 'NEW', color: 'Shadow' },
        { name: 'Platform Boots', price: 7500, image: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=500&h=650&fit=crop', tag: 'LIMITED', color: 'Chrome' },
    ];

    const mobileTestimonials = [
        { text: 'Finally a brand that gets it. No compromise, no mainstream BS. The leather jacket is built to last a lifetime. This is real fashion.', name: 'Vikram D.', city: 'Mumbai Underground', rating: 5 },
        { text: 'I wore the skull tee to a gig and three people asked where I got it. The quality is insane for the price. Zero regrets.', name: 'Anika R.', city: 'Bangalore Punk Scene', rating: 5 },
        { text: 'The cargo pants have become my daily uniform. Dark, functional, and built different. REVOLT understands the assignment.', name: 'Karan S.', city: 'Delhi Streets', rating: 5 },
    ];

    const mobileFeatures = [
        { icon: Zap, title: 'Limited Drops', desc: 'Once gone, gone forever' },
        { icon: Shield, title: 'Built Tough', desc: 'Heavy-duty construction' },
        { icon: Flame, title: 'No Rules', desc: 'Anti-trend philosophy' },
        { icon: Skull, title: 'Dark Aesthetic', desc: 'Unapologetically bold' },
    ];

    const instagramPosts = [
        'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1542272604-787c3835535d?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&h=400&fit=crop',
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial(prev => (prev + 1) % mobileTestimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [mobileTestimonials.length]);

    const handleTestimonialSwipe = (e, type) => {
        if (type === 'start') touchStartX.current = e.touches[0].clientX;
        if (type === 'end') {
            const diff = touchStartX.current - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
                setActiveTestimonial(prev => diff > 0 ? (prev + 1) % mobileTestimonials.length : (prev - 1 + mobileTestimonials.length) % mobileTestimonials.length);
            }
        }
    };

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* ==================== TOP BAR ==================== */}
            {/* Mobile Top Bar */}
            <div className="lg:hidden bg-red-600 text-white text-center py-2 text-[10px] sm:text-xs tracking-wider font-black px-4 uppercase">
                Free Shipping Over Rs.1,999 | Limited Drops Every Friday
            </div>
            {/* Desktop Top Bar - EXACT original */}
            <div className="hidden lg:block bg-black text-white text-center py-2.5 text-xs tracking-wider font-medium">
                FREE SHIPPING ON ORDERS OVER ₹1,499 | EASY 30-DAY RETURNS
            </div>

            {/* ==================== HEADER ==================== */}
            {/* Mobile Header */}
            <header className="lg:hidden bg-zinc-950/95 backdrop-blur-md sticky top-0 z-50 border-b border-zinc-800">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
                        <button className="min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
                        </button>
                        <h1 onClick={() => navigate('/template19')} className="text-xl sm:text-2xl font-black tracking-tighter text-white cursor-pointer uppercase">
                            REVOLT
                        </h1>
                        <div className="flex items-center gap-2 sm:gap-4">
                            <button className="min-w-[44px] min-h-[44px] flex items-center justify-center"><Search className="w-5 h-5 text-zinc-400 hover:text-white transition-colors" /></button>
                            <button className="min-w-[44px] min-h-[44px] hidden sm:flex items-center justify-center" onClick={() => navigate('/template19/profile')}><User className="w-5 h-5 text-zinc-400 hover:text-white transition-colors" /></button>
                            <button className="relative min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => navigate('/template19/cart')}>
                                <ShoppingBag className="w-5 h-5 text-zinc-400 hover:text-white transition-colors" />
                                <span className="absolute top-1.5 right-0.5 w-4 h-4 bg-red-600 text-white text-[10px] rounded-full flex items-center justify-center font-black">0</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className={`bg-zinc-950 border-t border-zinc-800 overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <nav className="px-5 py-6 space-y-1">
                        <button onClick={() => { navigate('/template19/shop'); setMobileMenuOpen(false); }} className="block w-full text-left py-3 text-base font-bold text-white border-b border-zinc-800 uppercase">Shop</button>
                        <button onClick={() => setMobileMenuOpen(false)} className="block w-full text-left py-3 text-base font-bold text-white border-b border-zinc-800 uppercase">Drops</button>
                        <button onClick={() => setMobileMenuOpen(false)} className="block w-full text-left py-3 text-base font-bold text-white border-b border-zinc-800 uppercase">Lookbook</button>
                        <button onClick={() => { navigate('/template19/contact'); setMobileMenuOpen(false); }} className="block w-full text-left py-3 text-base font-bold text-white border-b border-zinc-800 uppercase">Contact</button>
                        <div className="flex gap-4 pt-4">
                            <button onClick={() => { navigate('/template19/profile'); setMobileMenuOpen(false); }} className="flex items-center gap-2 text-sm text-zinc-400 font-bold"><User className="w-4 h-4" /> Account</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Desktop Header - EXACT original */}
            <header className="hidden lg:block bg-white sticky top-0 z-50 border-b border-gray-100">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-center justify-between px-4 lg:px-8 py-4">
                        {/* Logo */}
                        <h1 onClick={() => navigate('/template19')} className="text-2xl lg:text-3xl font-black tracking-tight text-black cursor-pointer">
                            ZEST
                        </h1>

                        {/* Desktop Nav */}
                        <nav className="flex items-center gap-8">
                            <button onClick={() => navigate('/template19/shop')} className="text-sm font-semibold text-gray-700 hover:text-black transition-colors">
                                SHOP ALL
                            </button>
                            <button className="text-sm font-semibold text-gray-700 hover:text-black transition-colors">
                                NEW ARRIVALS
                            </button>
                            <button className="text-sm font-semibold text-gray-700 hover:text-black transition-colors">
                                BESTSELLERS
                            </button>
                            <button className="text-sm font-semibold text-gray-700 hover:text-black transition-colors">
                                SALE
                            </button>
                        </nav>

                        {/* Icons */}
                        <div className="flex items-center gap-4">
                            <Search className="w-5 h-5 text-gray-700 cursor-pointer hover:text-black transition-colors" />
                            <User className="w-5 h-5 text-gray-700 cursor-pointer hover:text-black transition-colors" onClick={() => navigate('/template19/profile')} />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template19/cart')}>
                                <ShoppingBag className="w-5 h-5 text-gray-700 hover:text-black transition-colors" />
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-black text-white text-[10px] rounded-full flex items-center justify-center font-bold">0</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* ==================== HERO ==================== */}
            {/* Mobile Hero - current stacked dark design */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5]">
                    <img src="https://images.unsplash.com/photo-1551028719-00167b16eac5?w=1920&q=80" alt="Revolt Collection" className="w-full h-full object-cover" />
                </div>
                <div className="bg-zinc-950 px-5 py-8 text-center">
                    <p className="text-red-500 text-[10px] tracking-[0.3em] font-black mb-2">DROP 007 // NOW LIVE</p>
                    <h2 className="text-3xl sm:text-4xl font-black text-white mb-3 leading-none uppercase tracking-tight">Break The<br />Rules</h2>
                    <p className="text-[14px] text-zinc-400 mb-6 max-w-sm mx-auto leading-relaxed">Anti-fashion for the unapologetic. Dark, raw, and built to outlast every trend cycle.</p>
                    <button onClick={() => navigate('/template19/shop')} className="w-full py-4 bg-red-600 text-white text-sm font-black tracking-wider uppercase hover:bg-red-500 transition-colors min-h-[48px]">
                        SHOP THE DROP
                    </button>
                </div>
            </section>

            {/* Desktop Hero - EXACT original */}
            <section className="hidden lg:block relative h-[90vh] bg-black">
                <img
                    src="https://images.unsplash.com/photo-1518310383802-640c2de311b2?w=1920&h=1080&fit=crop"
                    alt="Hero"
                    className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>

                <div className="absolute inset-0 flex items-center">
                    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 w-full">
                        <div className="max-w-xl">
                            <p className="text-lime-400 text-sm font-bold tracking-wider mb-4">NEW COLLECTION</p>
                            <h1 className="text-5xl lg:text-7xl font-black text-white mb-6 leading-none">
                                MOVE WITH<br />
                                <span className="text-lime-400">POWER</span>
                            </h1>
                            <p className="text-gray-300 text-lg mb-8 max-w-md">
                                Performance activewear designed for women who push limits. Comfort meets style.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <button
                                    onClick={() => navigate('/template19/shop')}
                                    className="group px-8 py-4 bg-lime-400 text-black font-bold tracking-wide hover:bg-lime-300 transition-colors inline-flex items-center gap-2"
                                >
                                    SHOP NOW <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="px-8 py-4 border-2 border-white text-white font-bold tracking-wide hover:bg-white hover:text-black transition-colors">
                                    LEARN MORE
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ==================== FEATURES BAR (desktop only) ==================== */}
            <section className="hidden lg:block bg-lime-400 py-4">
                <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
                    <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16">
                        {desktopFeatures.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                                <feature.icon className="w-5 h-5 text-black" />
                                <div>
                                    <span className="font-bold text-black text-sm">{feature.title}</span>
                                    <span className="text-black/70 text-sm ml-2">{feature.desc}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== CATEGORIES ==================== */}
            {/* Mobile Categories - horizontal scroll dark */}
            <section className="lg:hidden py-10 sm:py-16 bg-zinc-900">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="px-5 sm:px-6 mb-6">
                        <p className="text-red-500 text-[10px] sm:text-xs font-black tracking-[0.3em] mb-1">CATEGORIES</p>
                        <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">Shop by Vibe</h2>
                    </div>
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {mobileCategories.map((cat, idx) => (
                            <div key={idx} className="w-[68vw] flex-shrink-0 snap-center group cursor-pointer">
                                <div className="relative aspect-[3/4] overflow-hidden rounded-2xl">
                                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 grayscale group-hover:grayscale-0" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/30 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-5">
                                        <p className="text-red-500 text-[10px] font-black tracking-[0.2em] mb-1">{cat.tagline}</p>
                                        <h3 className="text-lg sm:text-xl font-black text-white uppercase tracking-tight">{cat.name}</h3>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="px-5 mt-3">
                        <div className="w-16 h-1 bg-zinc-800 rounded-full mx-auto"><div className="w-6 h-full bg-red-600 rounded-full" /></div>
                    </div>
                </div>
            </section>

            {/* Desktop Shop by Category - EXACT original */}
            <section className="hidden lg:block py-24">
                <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <p className="text-lime-500 text-sm font-bold tracking-wider mb-2">CATEGORIES</p>
                            <h2 className="text-4xl font-black text-black">Shop by Style</h2>
                        </div>
                        <button className="flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-black transition-colors">
                            VIEW ALL <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-3 gap-6">
                        {desktopCollections.map((col, idx) => (
                            <div key={idx} className="group cursor-pointer relative overflow-hidden">
                                <div className="aspect-[4/5] overflow-hidden">
                                    <img
                                        src={col.image}
                                        alt={col.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <p className="text-lime-400 text-xs font-bold tracking-wider mb-1">{col.tagline.toUpperCase()}</p>
                                    <h3 className="text-2xl font-black text-white mb-2">{col.name}</h3>
                                    <p className="text-white/70 text-sm">{col.count} products</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== VIDEO (desktop only) ==================== */}
            <section className="hidden lg:block relative h-[60vh] bg-black">
                <img
                    src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1600&h=900&fit=crop"
                    alt="Video"
                    className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                        <button className="w-20 h-20 bg-lime-400 rounded-full flex items-center justify-center mb-6 mx-auto hover:scale-110 transition-transform">
                            <Play className="w-8 h-8 text-black ml-1" />
                        </button>
                        <p className="text-lime-400 text-sm font-bold tracking-wider mb-2">WATCH</p>
                        <h2 className="text-5xl font-black text-white">The Movement</h2>
                    </div>
                </div>
            </section>

            {/* ==================== PRODUCTS ==================== */}
            {/* Mobile Products - horizontal scroll dark */}
            <section className="lg:hidden py-10 sm:py-16 bg-zinc-950">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-end justify-between px-5 sm:px-6 mb-6 sm:mb-10">
                        <div>
                            <p className="text-red-500 text-[10px] sm:text-xs font-black tracking-[0.3em] mb-1">TRENDING</p>
                            <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">Bestsellers</h2>
                        </div>
                    </div>
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {mobileProducts.map((product, idx) => (
                            <div key={idx} className="w-[44vw] flex-shrink-0 snap-start group bg-zinc-900">
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    {product.tag && <span className="absolute top-2 left-2 bg-red-600 text-white text-[9px] sm:text-[10px] font-black tracking-wider px-2 py-1">{product.tag}</span>}
                                    <button onClick={() => setWishlist(prev => ({ ...prev, [idx]: !prev[idx] }))} className="absolute top-2 right-2 w-8 h-8 bg-zinc-900/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                                        <Heart className={`w-3.5 h-3.5 ${wishlist[idx] ? 'fill-red-500 text-red-500' : 'text-zinc-400'}`} />
                                    </button>
                                    <button className="absolute bottom-2 right-2 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center shadow-md sm:hidden">
                                        <Plus className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                                <div className="p-3">
                                    <p className="text-[9px] sm:text-[10px] text-red-500 font-bold tracking-wider mb-0.5">{product.color.toUpperCase()}</p>
                                    <h3 className="text-xs sm:text-sm font-bold text-white mb-0.5 leading-tight">{product.name}</h3>
                                    <p className="text-xs sm:text-sm text-zinc-400 font-medium">{'\u20B9'}{product.price.toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-6 px-5">
                        <button onClick={() => navigate('/template19/shop')} className="text-sm font-black text-red-500 tracking-wider border-b-2 border-red-600 pb-1 min-h-[44px] inline-flex items-center uppercase">View All</button>
                    </div>
                </div>
            </section>

            {/* Desktop Products Grid - EXACT original */}
            <section className="hidden lg:block py-24 bg-gray-50">
                <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <p className="text-lime-500 text-sm font-bold tracking-wider mb-2">TRENDING NOW</p>
                            <h2 className="text-4xl font-black text-black">Bestsellers</h2>
                        </div>
                        <button onClick={() => navigate('/template19/shop')} className="flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-black transition-colors">
                            SHOP ALL <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-4 gap-6">
                        {desktopProducts.map((product, idx) => (
                            <div
                                key={idx}
                                className="group bg-white"
                                onMouseEnter={() => setHoveredProduct(idx)}
                                onMouseLeave={() => setHoveredProduct(null)}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={hoveredProduct === idx ? product.hoverImage : product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-all duration-500"
                                    />
                                    {product.tag && (
                                        <span className="absolute top-3 left-3 bg-black text-white text-[10px] font-bold tracking-wider px-3 py-1.5">{product.tag.toUpperCase()}</span>
                                    )}
                                    <button className="absolute top-3 right-3 w-9 h-9 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-sm">
                                        <Heart className="w-4 h-4 text-gray-700" />
                                    </button>
                                    <button className="absolute bottom-3 left-3 right-3 py-3 bg-black text-white text-xs font-bold tracking-wider opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2">
                                        <Plus className="w-4 h-4" /> QUICK ADD
                                    </button>
                                </div>
                                <div className="p-4">
                                    <div className="flex gap-1.5 mb-2">
                                        {product.colors.map((color, i) => (
                                            <span key={i} className="w-3 h-3 rounded-full border border-gray-200" style={{ backgroundColor: color }}></span>
                                        ))}
                                    </div>
                                    <h3 className="text-sm font-bold text-black mb-1">{product.name}</h3>
                                    <p className="text-sm text-gray-700 font-medium">₹{product.price.toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== BRAND STORY (mobile) ==================== */}
            <section className="lg:hidden py-10 sm:py-16 bg-zinc-900">
                <div>
                    <img src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=1920&q=80" alt="The Movement" className="w-full aspect-[4/5] object-cover grayscale" />
                    <div className="px-5 py-8">
                        <p className="text-red-500 text-[10px] font-black tracking-[0.3em] mb-2">THE MANIFESTO</p>
                        <h2 className="text-xl font-black text-white mb-4 uppercase tracking-tight">We Don't Follow Trends. We Destroy Them.</h2>
                        <p className="text-[14px] text-zinc-400 leading-relaxed mb-4">REVOLT was born in the back alleys of Mumbai's underground scene. We make clothes for people who refuse to fit in, who choose darkness over the spotlight.</p>
                        <p className="text-[14px] text-zinc-400 leading-relaxed mb-6">Every piece is constructed for durability, designed for rebellion, and finished with an edge that mainstream fashion is too afraid to touch.</p>
                        <button className="text-sm font-black text-red-500 tracking-wider border-b-2 border-red-600 pb-1 min-h-[44px] inline-flex items-center uppercase">Read The Manifesto</button>
                    </div>
                </div>
            </section>

            {/* ==================== TESTIMONIALS / REVIEWS ==================== */}
            {/* Mobile Testimonials - swipeable */}
            <section className="lg:hidden py-10 sm:py-16 bg-zinc-950">
                <div className="max-w-screen-xl mx-auto px-5 sm:px-6">
                    <div className="text-center mb-8">
                        <p className="text-red-500 text-[10px] sm:text-xs font-black tracking-[0.3em] mb-2">FROM THE CREW</p>
                        <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">Real Talk</h2>
                    </div>
                    <div className="overflow-hidden" onTouchStart={e => handleTestimonialSwipe(e, 'start')} onTouchEnd={e => handleTestimonialSwipe(e, 'end')}>
                        <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                            {mobileTestimonials.map((t, idx) => (
                                <div key={idx} className="w-full flex-shrink-0 px-2">
                                    <div className="bg-zinc-900 p-6 border border-zinc-800">
                                        <div className="flex gap-1 mb-4">{[...Array(t.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-red-500 text-red-500" />)}</div>
                                        <p className="text-[14px] text-zinc-300 leading-relaxed mb-5">"{t.text}"</p>
                                        <p className="text-white font-bold text-sm">{t.name}</p>
                                        <p className="text-zinc-500 text-xs mt-1">{t.city}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-6">
                            {mobileTestimonials.map((_, idx) => (
                                <button key={idx} onClick={() => setActiveTestimonial(idx)} className={`w-2 h-2 rounded-full transition-all ${activeTestimonial === idx ? 'bg-red-600 w-6' : 'bg-zinc-700'}`} />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop Reviews - EXACT original grid */}
            <section className="hidden lg:block py-24">
                <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
                    <div className="text-center mb-12">
                        <p className="text-lime-500 text-sm font-bold tracking-wider mb-2">REVIEWS</p>
                        <h2 className="text-4xl font-black text-black">What Our Customers Say</h2>
                    </div>

                    <div className="grid grid-cols-3 gap-6">
                        {desktopReviews.map((review, idx) => (
                            <div key={idx} className="bg-gray-50 p-6">
                                <div className="flex gap-1 mb-4">
                                    {[...Array(review.rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-lime-400 text-lime-400" />
                                    ))}
                                </div>
                                <p className="text-gray-700 mb-4 leading-relaxed">"{review.text}"</p>
                                <p className="font-bold text-black">{review.name}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== FEATURES (mobile only) ==================== */}
            <section className="lg:hidden py-10 sm:py-16 bg-zinc-900 border-y border-zinc-800">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex overflow-x-auto gap-4 px-5 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {mobileFeatures.map((f, idx) => (
                            <div key={idx} className="w-[60vw] flex-shrink-0 snap-center text-center p-6 border border-zinc-800">
                                <f.icon className="w-6 h-6 text-red-500 mx-auto mb-3" strokeWidth={2} />
                                <h3 className="text-sm sm:text-base font-black text-white mb-1 uppercase tracking-wider">{f.title}</h3>
                                <p className="text-[14px] text-zinc-500">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== INSTAGRAM (mobile only) ==================== */}
            <section className="lg:hidden py-10 sm:py-16 bg-zinc-950">
                <div className="max-w-screen-2xl mx-auto px-5 sm:px-6">
                    <div className="text-center mb-6">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Instagram className="w-4 h-4 text-zinc-400" />
                            <span className="text-xs text-zinc-500 tracking-[0.2em]">@revolt.official</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">The Feed</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                        {instagramPosts.map((post, idx) => (
                            <div key={idx} className="aspect-square group cursor-pointer overflow-hidden">
                                <img src={post} alt={`Revolt ${idx + 1}`} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-500" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== NEWSLETTER ==================== */}
            {/* Mobile Newsletter */}
            <section className="lg:hidden py-10 sm:py-16 bg-red-600">
                <div className="max-w-xl mx-auto px-5 sm:px-6 text-center">
                    <h2 className="text-xl sm:text-2xl font-black text-white mb-3 uppercase tracking-tight">Join The Revolt</h2>
                    <p className="text-red-100 text-[14px] sm:text-sm mb-6 sm:mb-8">Get drop alerts, exclusive access, and 15% off your first order. No spam. Ever.</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input type="email" placeholder="Enter your email" className="flex-1 px-5 py-4 bg-red-700 border border-red-500 text-white text-sm placeholder-red-300 focus:outline-none focus:ring-2 focus:ring-white min-h-[48px]" />
                        <button className="px-8 py-4 bg-zinc-950 text-white text-sm font-black tracking-wider uppercase hover:bg-zinc-900 transition-colors w-full sm:w-auto min-h-[48px]">SUBSCRIBE</button>
                    </div>
                </div>
            </section>

            {/* Desktop CTA Banner - EXACT original */}
            <section className="hidden lg:block bg-black py-20">
                <div className="max-w-screen-xl mx-auto px-4 lg:px-8 text-center">
                    <h2 className="text-5xl font-black text-white mb-6">
                        JOIN THE <span className="text-lime-400">MOVEMENT</span>
                    </h2>
                    <p className="text-gray-400 mb-8 max-w-lg mx-auto">
                        Subscribe for exclusive drops, workout tips, and 10% off your first order.
                    </p>
                    <div className="flex gap-3 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-5 py-4 bg-gray-900 border-0 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-lime-400"
                        />
                        <button className="px-8 py-4 bg-lime-400 text-black text-sm font-bold tracking-wide hover:bg-lime-300 transition-colors">
                            SUBSCRIBE
                        </button>
                    </div>
                </div>
            </section>

            {/* ==================== FOOTER ==================== */}
            {/* Mobile Footer */}
            <footer className="lg:hidden bg-zinc-950 border-t border-zinc-800">
                <div className="max-w-screen-2xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
                    <div className="grid grid-cols-2 gap-8">
                        <div className="col-span-2">
                            <h3 className="text-2xl font-black mb-3 text-white uppercase tracking-tighter">REVOLT</h3>
                            <p className="text-zinc-500 text-sm leading-relaxed">Anti-fashion for the unapologetic. Born in the underground. Made in India.</p>
                        </div>
                        <div>
                            <h4 className="text-xs font-black tracking-wider mb-3 text-zinc-400 uppercase">Shop</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template19/shop')} className="text-zinc-500 hover:text-red-500 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">All Products</span></li>
                                <li><span className="text-zinc-500 hover:text-red-500 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">New Drops</span></li>
                                <li><span className="text-zinc-500 hover:text-red-500 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Bestsellers</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-xs font-black tracking-wider mb-3 text-zinc-400 uppercase">Help</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template19/contact')} className="text-zinc-500 hover:text-red-500 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Contact</span></li>
                                <li><span onClick={() => navigate('/template19/orders')} className="text-zinc-500 hover:text-red-500 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Track Order</span></li>
                                <li><span className="text-zinc-500 hover:text-red-500 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Returns</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-xs font-black tracking-wider mb-3 text-zinc-400 uppercase">Connect</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-zinc-500">revolt@revolt.in</li>
                                <li className="text-zinc-500">+91 98765 43210</li>
                                <li className="text-zinc-500 pt-1">24/7 Online</li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-zinc-800 mt-8 pt-6 flex flex-col items-center gap-4">
                        <p className="text-zinc-600 text-xs">&copy; 2025 REVOLT. No rights reserved.</p>
                        <div className="flex gap-6 text-xs text-zinc-600">
                            <span className="hover:text-red-500 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Privacy</span>
                            <span className="hover:text-red-500 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Terms</span>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Desktop Footer - EXACT original */}
            <footer className="hidden lg:block bg-gray-900 text-white">
                <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-16">
                    <div className="grid grid-cols-4 gap-12">
                        <div>
                            <h3 className="text-2xl font-black mb-4">ZEST</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Performance activewear for women who move. Designed in India.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-xs font-bold tracking-wider mb-4 text-gray-300">SHOP</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template19/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">All Products</span></li>
                                <li><span className="text-gray-400 hover:text-white cursor-pointer transition-colors">New Arrivals</span></li>
                                <li><span className="text-gray-400 hover:text-white cursor-pointer transition-colors">Bestsellers</span></li>
                                <li><span className="text-gray-400 hover:text-white cursor-pointer transition-colors">Sale</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs font-bold tracking-wider mb-4 text-gray-300">HELP</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template19/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template19/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Track Order</span></li>
                                <li><span className="text-gray-400 hover:text-white cursor-pointer transition-colors">Size Guide</span></li>
                                <li><span className="text-gray-400 hover:text-white cursor-pointer transition-colors">Returns</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs font-bold tracking-wider mb-4 text-gray-300">CONNECT</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">hello@zest.in</li>
                                <li className="text-gray-400">+91 98765 43210</li>
                                <li className="text-gray-400 pt-2">Mon - Sat: 10am - 7pm</li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 mt-12 pt-8 flex justify-between items-center gap-4">
                        <p className="text-gray-500 text-xs">© 2025 Zest. All rights reserved.</p>
                        <div className="flex gap-6 text-xs text-gray-500">
                            <span className="hover:text-white cursor-pointer transition-colors">Privacy</span>
                            <span className="hover:text-white cursor-pointer transition-colors">Terms</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template19;